import * as d3 from "d3";
import { useEffect, useRef, ReactElement, useState } from "react";
import React from "react";

/*
interface Props {
  data: {
    channel_width: number
    erosion: {
      inner: number,
      outer: number
    }
    height: number
  }
  labels: {
    x: string,
    y: string
  }
  legend: {
    names: string[];
  }
  yaxis?: {
    invert?: boolean
  }
}*/

const original_width = 1000;
const original_height = 300;

const margin = { top: 10, right: 10, bottom: 40, left: 10 };
const width = original_width - margin.left - margin.right;
const height = original_height - margin.top - margin.bottom;

export default function BankDiagram(props) {

  const axisBottomRef = useRef(null);
  const axisMiddleRef = useRef(null);
  const bankRef = useRef(null);
  const [noVeggieSelected, setNoVeggieSelected] = React.useState(false);

  useEffect(() => {

    const x_min = -7;
    const x_max = 7;
    const y_max = 5;

    const scaleX = d3.scaleLinear().domain([x_min, x_max]).range([0, width]);

    const scaleY = d3
      .scaleLinear()
      .domain([y_max, 0])
      .range([0, height]);

    if (axisBottomRef.current) {
      d3.select(axisBottomRef.current).selectAll("*").remove();
      d3.select(axisBottomRef.current).call(d3.axisBottom(scaleX));

    }

    if (axisMiddleRef.current) {
      d3.select(axisMiddleRef.current).selectAll("*").remove();
      d3.select(axisMiddleRef.current).call(d3.axisRight(scaleY).ticks(0).tickSize(0));
    }

    if (bankRef.current) {
      d3.select(bankRef.current).selectAll("*").remove();

      var line = d3.line()
        .x(function(d) {
          return d.x;
        })
        .y(function(d) {
          return d.y;
        });


      const top_extra = 1.5;
      const point_length = .13;



      d3.select(bankRef.current).append("rect")
        .attr("x", scaleX(x_min))
        .attr("y", scaleY((top_extra) * Math.sqrt(3)))
        .attr("height", height - scaleY((top_extra) * Math.sqrt(3)))
        .attr("width", width)
        .attr("stroke", "grey")
        .style("fill", "saddlebrown");


      // Left side of grass
      const grass_height = 4;
      d3.select(bankRef.current).append("rect")
        .attr("x", scaleX(x_min))
        .attr("y", scaleY(top_extra * Math.sqrt(3)) - grass_height)
        .attr("height", grass_height)
        .attr("width", scaleX(- props.data.channel_width / 2 - top_extra - props.data.erosion.outer))
        .attr("stroke", "grey")
        .style("fill", "darkgreen");


      // Right side of grass
      d3.select(bankRef.current).append("rect")
        .attr("x", scaleX(props.data.channel_width / 2 + props.data.erosion.inner + top_extra))
        .attr("y", scaleY(top_extra * Math.sqrt(3)) - grass_height)
        .attr("height", grass_height)
        .attr("width", scaleX(x_max) - scaleX(props.data.channel_width / 2 + top_extra + props.data.erosion.inner))
        .attr("stroke", "grey")
        .style("fill", "darkgreen");


      // The shape after erosion...
      var points = [{
        x: scaleX(- props.data.channel_width / 2 - props.data.erosion.outer), y: scaleY(0) // starting point
      }, {
        x: scaleX(- props.data.channel_width / 2 - props.data.erosion.outer - top_extra), y: scaleY(top_extra * Math.sqrt(3)) // top left
      }, {
        x: scaleX(- props.data.channel_width / 2 - props.data.erosion.outer - point_length - top_extra), y: scaleY((point_length + top_extra) * Math.sqrt(3)) // top left point
      }, {
        x: scaleX(- props.data.channel_width / 2 - props.data.erosion.outer - top_extra), y: scaleY(top_extra * Math.sqrt(3)) // back to top left
      },
      {
        x: scaleX(props.data.channel_width / 2 + props.data.erosion.inner + top_extra), y: scaleY(top_extra * Math.sqrt(3)) // top right
      }, {
        x: scaleX(props.data.channel_width / 2 + props.data.erosion.inner + point_length + top_extra), y: scaleY((point_length + top_extra) * Math.sqrt(3)) // top left point
      }, {
        x: scaleX(props.data.channel_width / 2 + props.data.erosion.inner), y: scaleY(0)
      }]

      d3.select(bankRef.current).append("path")
        .attr("d", line(points) + 'Z')
        .style("stroke", "black")
        .style("fill", "orange");


      // The inner diagram, i.e. original stream
      var points = [{
        x: scaleX(- props.data.channel_width / 2), y: scaleY(0) // starting point
      }, {
        x: scaleX(- props.data.channel_width / 2 - top_extra), y: scaleY(top_extra * Math.sqrt(3)) // top left
      }, {
        x: scaleX(- props.data.channel_width / 2 - point_length - top_extra), y: scaleY((point_length + top_extra) * Math.sqrt(3)) // top left point
      }, {
        x: scaleX(- props.data.channel_width / 2 - top_extra), y: scaleY(top_extra * Math.sqrt(3)) // back to top left
      },
      {
        x: scaleX(props.data.channel_width / 2 + top_extra), y: scaleY(top_extra * Math.sqrt(3)) // top right
      }, {
        x: scaleX(props.data.channel_width / 2 + point_length + top_extra), y: scaleY((point_length + top_extra) * Math.sqrt(3)) // top left point
      }, {
        x: scaleX(props.data.channel_width / 2), y: scaleY(0)
      }]
      d3.select(bankRef.current).append("path")
        .attr("d", line(points) + 'Z')
        .style("stroke", "black")
        .style("fill", "lightblue");

      d3.select(bankRef.current).append("text")
        .attr("x", scaleX(x_min + .5))
        .attr("y", scaleY((.20 + top_extra) * Math.sqrt(3)))
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', '1em')
        .attr("fill", "currentColor")
        .text("Outer");

      d3.select(bankRef.current).append("text")
        .attr("x", scaleX(x_max - .5))
        .attr("y", scaleY((.20 + top_extra) * Math.sqrt(3)))
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', '1em')
        .attr("fill", "currentColor")
        .text("Inner");

      // Orange box + Inner bank erosion text
      d3.select(bankRef.current).append("rect")
        .attr("x", scaleX(props.data.channel_width / 2 + props.data.erosion.inner / 2 + top_extra) - 100)
        .attr("y", scaleY((.40 + top_extra) * Math.sqrt(3)) - 9)
        .attr("height", 10)
        .attr("width", 10)
        .attr("stroke", "grey")
        .style("fill", "orange");

      d3.select(bankRef.current).append("text")
        .attr("x", scaleX(props.data.channel_width / 2 + top_extra + (props.data.erosion.inner / 2)))
        .attr("y", scaleY((.40 + top_extra) * Math.sqrt(3)))
        .attr('text-anchor', 'middle')
        .attr('font-size', '.8em')
        .attr("fill", "currentColor")
        .text("Inner bank erosion: " + props.data.erosion.inner.toFixed(2) + " m");

      // Orange box + Outer bank erosion text
      d3.select(bankRef.current).append("rect")
        .attr("x", scaleX(-props.data.channel_width / 2 - props.data.erosion.outer / 2 - top_extra) - 103)
        .attr("y", scaleY((.40 + top_extra) * Math.sqrt(3)) - 9)
        .attr("height", 10)
        .attr("width", 10)
        .attr("stroke", "grey")
        .style("fill", "orange");
      d3.select(bankRef.current).append("text")
        .attr("x", scaleX(-props.data.channel_width / 2 - top_extra - (props.data.erosion.outer / 2)))
        .attr("y", scaleY((.40 + top_extra) * Math.sqrt(3)))
        .attr('text-anchor', 'middle')
        .attr('font-size', '.8em')
        .attr("fill", "currentColor")
        .text("Outer bank erosion: " + props.data.erosion.outer.toFixed(2) + " m");

      d3.select(bankRef.current).append("text")
        .attr("x", scaleX(.14))
        .attr("y", scaleY(-0.32))
        .attr('text-anchor', 'middle')
        .attr('font-size', '0.6em')
        .attr("fill", "currentColor")
        .text("m");






      // Legend
      const legend_height = height + 35;
      const legend_width = 39;
      const veggie_origin = width / 2 + 100;
      const veggie_color = "limegreen";
      const noveggie_origin = width / 2 - 50;
      const noveggie_color = "black";

      d3.select(bankRef.current).append("rect")
        .attr("x", (noVeggieSelected ? veggie_origin : noveggie_origin) - 80)
        .attr("y", legend_height - 5)
        .attr("height", 2)
        .attr("width", 115)
        .attr("stroke", "grey")
        .style("fill", "grey");


      d3.select(bankRef.current).append("rect")
        .attr("x", veggie_origin - 80)
        .attr("y", legend_height - 5)
        .attr("height", 2)
        .attr("width", legend_width)
        .attr("stroke", veggie_color)
        .style("fill", veggie_color);

      d3.select(bankRef.current).append("text")
        .attr("x", veggie_origin)
        .attr("y", legend_height)
        .attr('text-anchor', 'middle')
        .attr('font-size', '.7em')
        .attr("fill", noVeggieSelected ? "gray" : "black")
        .text("Vegetation");
      d3.select(bankRef.current).append("rect")
        .attr("x", veggie_origin - 80)
        .attr("y", legend_height - 10)
        .attr("height", 14)
        .attr("width", 115)
        //.attr("stroke", veggie_color)
        .style("fill", veggie_color)
        .style("opacity", 0)
        .on("click", function(d) {
          if (props.callback !== undefined && props.callback.on_veggie !== undefined) {
            setNoVeggieSelected(false);
            props.callback.on_veggie();
          }
        });



      d3.select(bankRef.current).append("rect")
        .attr("x", noveggie_origin - 90)
        .attr("y", legend_height - 5)
        .attr("height", 2)
        .attr("width", legend_width)
        .attr("stroke", noveggie_color)
        .style("fill", noveggie_color);
      d3.select(bankRef.current).append("text")
        .attr("x", noveggie_origin)
        .attr("y", legend_height)
        .attr('text-anchor', 'middle')
        .attr('font-size', '.7em')
        .attr("fill", noVeggieSelected ? "black" : "grey")
        .text("No Vegetation");
      d3.select(bankRef.current).append("rect")
        .attr("x", noveggie_origin - 80)
        .attr("y", legend_height - 10)
        .attr("height", 14)
        .attr("width", 115)
        //.attr("stroke", veggie_color)
        .style("fill", noveggie_color)
        .style("opacity", 0)
        .on("click", function(d) {
          if (props.callback !== undefined && props.callback.on_noveggie !== undefined) {
            setNoVeggieSelected(true);
            props.callback.on_noveggie();
          }
        });



    }

  }, [props.data]);

  return (
    <svg
      style={{
        height: "100%",
        marginRight: "0px",
        marginLeft: "0px",
        marginBottom: `${margin.bottom}px`
      }}
      viewBox={`0 0 ${original_width} ${original_height}`}
      id="landslide_thickness"
    >
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        <g key="axisBottom" ref={axisBottomRef} transform={`translate(0, ${height})`} />
        <g key="axisMiddle" ref={axisMiddleRef} transform={`translate(${width / 2}, 0)`} />
        <g ref={bankRef} />
      </g>
    </svg>
  );
}

